import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Link as MuiLink,
  Pagination,
  type SelectChangeEvent,
} from '@mui/material';
import { getAMIsByAccountId, getAMICountByAccountId } from '../services/api';
import type { AMIResponse } from '../types/api';
import Navbar from '../components/Navbar';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return 'Not available';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    return date.toISOString().slice(0, 10).replace(/-/g, '');
  } catch {
    return 'Invalid date';
  }
};

const formatDateVerbose = (dateString: string | undefined): string => {
  if (!dateString) return 'Not available';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  } catch {
    return 'Invalid date';
  }
};

const getMostCommonPatterns = (amis: AMIResponse[]): string => {
  if (amis.length === 0) return 'No patterns found';
  const patterns = amis
    .filter(ami => ami.name && typeof ami.name === 'string')
    .map(ami => ami.name.split('-')[0]); // Get the prefix before the first hyphen
  
  if (patterns.length === 0) return 'No patterns found';
  
  const patternCounts = patterns.reduce((acc, pattern) => {
    acc[pattern] = (acc[pattern] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const sorted = Object.entries(patternCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3);
  return sorted.length > 0
    ? sorted.map(([pattern, count]) => `${pattern} (${count} AMIs)`).join(', ')
    : 'No patterns found';
};

const SORTABLE_FIELDS = [
  { value: 'lastSeen', label: 'Last Seen' },
  { value: 'firstSeen', label: 'First Seen' },
  { value: 'name', label: 'Name' },
  { value: 'regionFound', label: 'Region' },
];

const AccountDetails = () => {
  const { accountId } = useParams<{ accountId: string }>();
  const [amis, setAMIs] = useState<AMIResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState('lastSeen');
  const [sortDirection, setSortDirection] = useState<'desc' | 'asc'>('desc');
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [totalCount, setTotalCount] = useState(0);

  // Fetch total count once
  useEffect(() => {
    const fetchCount = async () => {
      if (!accountId) return;
      try {
        const countResponse = await getAMICountByAccountId(accountId);
        if (!countResponse.error) {
          setTotalCount(countResponse.data.count);
        }
      } catch (err) {
        console.error('Failed to fetch AMI count:', err);
      }
    };
    fetchCount();
  }, [accountId]);

  // Fetch paginated AMIs
  useEffect(() => {
    const fetchData = async () => {
      if (!accountId) return;

      setLoading(true);
      setError(null);

      try {
        const offset = (page - 1) * pageSize;
        const response = await getAMIsByAccountId(accountId, pageSize, offset);
        if (response.error) {
          setError(response.error);
        } else {
          setAMIs(response.data.amis || []);
          setTotalCount(response.data.pagination.total);
        }
      } catch (err) {
        setError('Failed to fetch account details');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [accountId, page, pageSize]);

  const handleSortFieldChange = (event: SelectChangeEvent) => {
    setSortField(event.target.value);
  };

  const handleSortDirectionChange = (
    _event: React.MouseEvent<HTMLElement>,
    newDirection: 'asc' | 'desc' | null
  ) => {
    if (newDirection) setSortDirection(newDirection);
  };

  const sortedAMIs = [...amis].sort((a, b) => {
    const aValue = a[sortField as keyof AMIResponse];
    const bValue = b[sortField as keyof AMIResponse];
    if (sortField === 'lastSeen' || sortField === 'firstSeen') {
      const aTime = new Date(String(aValue)).getTime();
      const bTime = new Date(String(bValue)).getTime();
      if (aTime < bTime) return sortDirection === 'asc' ? -1 : 1;
      if (aTime > bTime) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    }
    if (aValue === undefined || bValue === undefined) return 0;
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (amis.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="warning">No AMIs found for this account</Alert>
      </Container>
    );
  }

  // Calculate metadata with proper date validation
  const validFirstSeenDates = amis
    .map(ami => ami.firstSeen ? new Date(ami.firstSeen) : null)
    .filter((date): date is Date => date !== null && !isNaN(date.getTime()));
    
  const validLastSeenDates = amis
    .map(ami => ami.lastSeen ? new Date(ami.lastSeen) : null)
    .filter((date): date is Date => date !== null && !isNaN(date.getTime()));

  const firstSeen = validFirstSeenDates.length > 0 
    ? validFirstSeenDates.reduce((earliest, current) => current < earliest ? current : earliest)
    : null;

  const lastSeen = validLastSeenDates.length > 0
    ? validLastSeenDates.reduce((latest, current) => current > latest ? current : latest)
    : null;

  const mostCommonPatterns = getMostCommonPatterns(amis);

  // Calculate region breakdown
  const regionCounts = amis.reduce((acc, ami) => {
    acc[ami.regionFound] = (acc[ami.regionFound] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const regionData: { name: string; value: number }[] = Object.entries(regionCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([region, count]) => ({ name: region, value: count }));
  const regionCount = regionData.length;
  const COLORS = ['#1976d2', '#dc004e', '#ffb300', '#388e3c', '#7b1fa2', '#0288d1', '#c2185b', '#afb42b', '#fbc02d', '#0097a7'];

  // Calculate creation date histogram (by month)
  const creationBuckets: Record<string, number> = {};
  amis.forEach((ami) => {
    if (ami.creationDate) {
      const d = new Date(ami.creationDate);
      if (!isNaN(d.getTime())) {
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        creationBuckets[key] = (creationBuckets[key] || 0) + 1;
      }
    }
  });
  const creationData = Object.entries(creationBuckets)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, count]) => ({ month, count }));

  return (
    <Container maxWidth="lg" sx={{ mt: 4, pb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', mb: 4 }}>
        <Navbar />
      </Box>
      <Typography variant="h4" gutterBottom>
        Account {accountId}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Account Overview
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Total AMIs
                </Typography>
                <Typography variant="h4">{totalCount}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  First AMI Seen
                </Typography>
                <Typography>{firstSeen ? formatDateVerbose(firstSeen.toISOString()) : 'Not available'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Most Recent AMI
                </Typography>
                <Typography>{lastSeen ? formatDateVerbose(lastSeen.toISOString()) : 'Not available'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Most Common Name Patterns
                </Typography>
                <Typography>{mostCommonPatterns}</Typography>
              </Grid>
            </Grid>
            <Box mt={2}>              
              <MuiLink
                href={`https://awseye.com/search?aid=${accountId}`}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
              >
                Look up {accountId} on awseye.com
              </MuiLink>
            </Box>
            <Box mt={4} display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={4}>
              <Paper sx={{ p: 2, flex: 1, minWidth: 250 }}>
                <Typography variant="subtitle1" gutterBottom>Region Breakdown</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Count of regions used by this account: {regionCount}
                </Typography>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={regionData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label>
                      {regionData.map((_, idx) => (
                        <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
              <Paper sx={{ p: 2, flex: 2, minWidth: 300 }}>
                <Typography variant="subtitle1" gutterBottom>AMI Creation Dates Over Time</Typography>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={creationData} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis allowDecimals={false} />
                    <RechartsTooltip />
                    <Line type="monotone" dataKey="count" stroke="#1976d2" strokeWidth={2} dot />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_e, v) => v && setViewMode(v)}
              size="small"
              aria-label="view mode"
              sx={{ mr: 2 }}
            >
              <ToggleButton value="table" aria-label="Table View">
                Table View
              </ToggleButton>
              <ToggleButton value="cards" aria-label="Card View">
                Card View
              </ToggleButton>
            </ToggleButtonGroup>
            <FormControl size="small">
              <InputLabel id="sort-field-label">Sort By</InputLabel>
              <Select
                labelId="sort-field-label"
                value={sortField}
                label="Sort By"
                onChange={handleSortFieldChange}
              >
                {SORTABLE_FIELDS.map((field) => (
                  <MenuItem key={field.value} value={field.value}>
                    {field.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <ToggleButtonGroup
              value={sortDirection}
              exclusive
              onChange={handleSortDirectionChange}
              size="small"
              aria-label="sort direction"
            >
              <ToggleButton value="asc" aria-label="ascending">
                Ascending
              </ToggleButton>
              <ToggleButton value="desc" aria-label="descending">
                Descending
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Typography variant="h5" gutterBottom>
            AMIs ({amis.length})
          </Typography>
          {viewMode === 'table' ? (
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Image ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Region</TableCell>
                    <TableCell>Last Seen</TableCell>
                    <TableCell>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedAMIs.map((ami) => (
                    <TableRow
                      key={ami.amiId}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => window.location.href = `/image/${ami.amiId}`}
                    >
                      <TableCell>{ami.amiId}</TableCell>
                      <TableCell>{ami.name || 'Unknown'}</TableCell>
                      <TableCell>{ami.regionFound || 'Unknown'}</TableCell>
                      <TableCell>{formatDate(ami.lastSeen)}</TableCell>
                      <TableCell sx={{ maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ami.description || 'No description'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Grid container spacing={2}>
              {sortedAMIs.map((ami) => (
                <Grid item xs={12} sm={6} md={4} key={ami.amiId}>
                  <Paper
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                    onClick={() => window.location.href = `/image/${ami.amiId}`}
                  >
                    <Typography variant="h6" noWrap>
                      {ami.name || 'Unknown'}
                    </Typography>
                    <Typography color="text.secondary" gutterBottom>
                      {ami.amiId}
                    </Typography>
                    <Typography variant="body2">
                      Region: {ami.regionFound || 'Unknown'}
                    </Typography>
                    {ami.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {ami.description}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Pagination Controls */}
          {totalCount > pageSize && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4, gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} of {totalCount} AMIs
              </Typography>
              <Pagination
                count={Math.ceil(totalCount / pageSize)}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default AccountDetails; 